import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DietaryMark from "@/components/DietaryMark";
import { Plus, Trash2, Save, Eye, EyeOff, Upload, Download, FileText, RotateCcw, ChevronDown, ChevronRight, Minimize2, Maximize2 } from "lucide-react";

const emptyForm = { section: "", name: "", description: "", price: "", dietary: "veg", order: 100, active: true };

export default function StaffMenu() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [draft, setDraft] = useState({});
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [resetting, setResetting] = useState(false);
  // Collapsible menu sections — persisted per user in localStorage so the
  // preferred collapse state survives page reloads.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem("tbb_menu_collapsed_sections");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const persistCollapsed = (set) => {
    try { localStorage.setItem("tbb_menu_collapsed_sections", JSON.stringify(Array.from(set))); } catch { /* ignore */ }
  };
  const toggleSection = (name) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      persistCollapsed(next);
      return next;
    });
  };
  const collapseAll = () => {
    const next = new Set(items.map((i) => i.section));
    setCollapsed(next); persistCollapsed(next);
  };
  const expandAll = () => {
    const next = new Set();
    setCollapsed(next); persistCollapsed(next);
  };
  const fileRef = useRef(null);

  const loadLastImport = async () => {
    try {
      const { data } = await api.get("/staff/menu-items/last-import");
      setLastImport(data);
    } catch { /* ignore — non-critical */ }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/staff/menu-items/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Menu-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(err); toast.error("Export failed");
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get("/staff/menu-items/export.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `TBB-Menu-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(err); toast.error("PDF download failed");
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading same file
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) { toast.error("Please pick a .csv file"); return; }
    const proceed = window.confirm(
      `Replace the entire menu with the contents of "${file.name}"?\n\n` +
      "This will DELETE all existing menu items and import the file's rows as the new full menu. " +
      "Tip: Export first if you want a backup."
    );
    if (!proceed) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post("/staff/menu-items/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Imported ${data.imported} items · previous menu cleared`);
      if (data.warnings?.length) console.warn("Import warnings:", data.warnings);
      await load();
      await loadLastImport();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Import failed");
    } finally { setImporting(false); }
  };

  const resetToLast = async () => {
    if (!lastImport?.available) return;
    const when = lastImport.uploaded_at ? new Date(lastImport.uploaded_at).toLocaleString("en-IN") : "";
    if (!window.confirm(
      `Reset the entire menu to the last uploaded CSV?\n\n` +
      `File: ${lastImport.filename || "—"}\n` +
      `Rows: ${lastImport.imported_count || "?"}\n` +
      `Uploaded: ${when}\n\n` +
      `This will DELETE the current menu and restore the last import.`
    )) return;
    setResetting(true);
    try {
      const { data } = await api.post("/staff/menu-items/reset-to-last");
      toast.success(`Restored ${data.restored} items from ${data.filename || "last import"}`);
      await load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Reset failed");
    } finally { setResetting(false); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/staff/menu-items");
      setItems(data);
      const d = {};
      data.forEach((it) => { d[it.id] = { ...it }; });
      setDraft(d);
    } catch { toast.error("Could not load menu."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); loadLastImport(); }, []);

  const sections = useMemo(() => Array.from(new Set(items.map((i) => i.section))), [items]);

  const grouped = useMemo(() => {
    const m = new Map();
    items.forEach((it) => {
      if (!m.has(it.section)) m.set(it.section, []);
      m.get(it.section).push(it);
    });
    return Array.from(m.entries());
  }, [items]);

  const update = (id, k, v) => setDraft((prev) => ({ ...prev, [id]: { ...prev[id], [k]: v } }));

  const save = async (id) => {
    try {
      const d = draft[id];
      const { data } = await api.patch(`/staff/menu-items/${id}`, {
        section: d.section, name: d.name, description: d.description, price: d.price,
        dietary: d.dietary || "", order: Number(d.order),
      });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      setDraft((prev) => ({ ...prev, [id]: { ...data } }));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const toggle = async (it) => {
    try {
      const { data } = await api.patch(`/staff/menu-items/${it.id}`, { active: !it.active });
      setItems((prev) => prev.map((x) => (x.id === it.id ? data : x)));
      toast.success(data.active ? "Visible on site" : "Hidden");
    } catch { toast.error("Toggle failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this dish?")) return;
    try {
      await api.delete(`/staff/menu-items/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/staff/menu-items", { ...form, order: Number(form.order) });
      toast.success("Dish added");
      setForm(emptyForm);
      setCreating(false);
      load();
    } catch { toast.error("Create failed"); }
  };

  return (
    <div data-testid="staff-menu">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow">À la carte</div>
          <h1 className="mt-2 text-4xl">Menu editor</h1>
          <p className="mt-3 text-[hsl(var(--ink-soft))] max-w-xl">
            Add or edit individual dishes. Group them by putting the same section name (e.g. &ldquo;Big Plates&rdquo;)
            on multiple items. Toggle a dish off to hide it from the public menu.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="menu-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--rule))] rounded hover:border-[hsl(var(--brass))] hover:text-[hsl(var(--brass-2))]">
            <Download size={14} /> Export CSV
          </button>
          <button data-testid="menu-export-pdf" onClick={exportPdf} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--forest))] rounded text-[hsl(var(--forest))] hover:bg-[hsl(var(--forest))] hover:text-[hsl(var(--bg))] transition-colors">
            <FileText size={14} /> Download PDF
          </button>
          <button data-testid="menu-import-btn" onClick={() => fileRef.current?.click()} disabled={importing} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--brass))] rounded text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-40">
            <Upload size={14} /> {importing ? "Importing…" : "Import CSV"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onImportFile} className="hidden" data-testid="menu-import-file" />
          {lastImport?.available && (
            <button
              data-testid="menu-reset-btn"
              onClick={resetToLast}
              disabled={resetting}
              title={`Reset to ${lastImport.filename || "last import"} · ${lastImport.imported_count || 0} rows · uploaded ${lastImport.uploaded_at ? new Date(lastImport.uploaded_at).toLocaleString("en-IN") : ""}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm border border-[hsl(var(--ink))] rounded text-[hsl(var(--ink))] hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors disabled:opacity-40"
            >
              <RotateCcw size={14} /> {resetting ? "Restoring…" : "Reset to last CSV"}
            </button>
          )}
          <button data-testid="menu-new-btn" onClick={() => setCreating((v) => !v)} className="btn-brass !py-2.5 !px-5 text-sm">
            <Plus size={14} /> {creating ? "Cancel" : "New dish"}
          </button>
        </div>
      </div>

      {creating && (
        <form data-testid="menu-new-form" onSubmit={create} className="mt-6 border border-[hsl(var(--brass))] bg-[hsl(var(--bg-2))] p-6 grid grid-cols-1 md:grid-cols-6 gap-3">
          <input required placeholder="Section" data-testid="new-menu-section" list="menu-sections" className="menu-inp md:col-span-2" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} />
          <datalist id="menu-sections">
            {sections.map((s) => <option key={s} value={s} />)}
          </datalist>
          <input required placeholder="Dish name" data-testid="new-menu-name" className="menu-inp md:col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <select data-testid="new-menu-dietary" className="menu-inp" value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })}>
            <option value="veg">Veg (green)</option>
            <option value="non-veg">Non-veg (red)</option>
            <option value="">None</option>
          </select>
          <input placeholder="Price e.g. ₹520" data-testid="new-menu-price" className="menu-inp" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <input placeholder="Description" data-testid="new-menu-description" className="menu-inp md:col-span-5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <input type="number" placeholder="Order" className="menu-inp" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          <div className="md:col-span-6">
            <button type="submit" data-testid="new-menu-submit" className="btn-brass">Add dish</button>
          </div>
        </form>
      )}

      {loading && <div className="mt-8 text-[hsl(var(--ink-soft))]">Loading...</div>}

      {/* Shared datalist for section autocompletion across all rows */}
      <datalist id="menu-sections-all">
        {sections.map((s) => <option key={s} value={s} />)}
      </datalist>

      {/* Quick-add chips: click a section to prefill the New-dish form with that section */}
      {!creating && sections.length > 0 && (
        <div data-testid="section-quick-adds" className="mt-6 flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Add to section:</span>
          {sections.map((s) => (
            <button
              key={s}
              data-testid={`section-chip-${s.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => { setForm({ ...emptyForm, section: s }); setCreating(true); }}
              className="text-[11px] px-2.5 py-1 rounded-full border border-[hsl(var(--rule))] hover:border-[hsl(var(--forest))] hover:text-[hsl(var(--forest))] transition-colors"
            >
              + {s}
            </button>
          ))}
          <button
            data-testid="section-new-btn"
            onClick={() => { setForm({ ...emptyForm, section: "" }); setCreating(true); setTimeout(() => document.querySelector('[data-testid="new-menu-section"]')?.focus(), 50); }}
            className="text-[11px] px-2.5 py-1 rounded-full border border-[hsl(var(--brass))] text-[hsl(var(--brass-2))] hover:bg-[hsl(var(--brass))] hover:text-[hsl(var(--bg))] transition-colors"
          >
            + New section…
          </button>
        </div>
      )}

      <div className="mt-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
            {grouped.length} section{grouped.length === 1 ? "" : "s"} · {items.length} dish{items.length === 1 ? "" : "es"}
          </div>
          <div className="flex gap-1">
            <button
              data-testid="menu-collapse-all"
              onClick={collapseAll}
              disabled={collapsed.size >= grouped.length}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[hsl(var(--rule))] rounded hover:bg-[hsl(var(--bg-2))] disabled:opacity-40"
              title="Collapse all sections"
            >
              <Minimize2 size={12} /> Collapse all
            </button>
            <button
              data-testid="menu-expand-all"
              onClick={expandAll}
              disabled={collapsed.size === 0}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-[hsl(var(--rule))] rounded hover:bg-[hsl(var(--bg-2))] disabled:opacity-40"
              title="Expand all sections"
            >
              <Maximize2 size={12} /> Expand all
            </button>
          </div>
        </div>

        {grouped.map(([sectionName, rows]) => {
          const isCollapsed = collapsed.has(sectionName);
          const activeCount = rows.filter((r) => r.active).length;
          return (
          <div key={sectionName} data-testid={`section-${sectionName.split(" ")[0].toLowerCase()}`}>
            <button
              type="button"
              data-testid={`section-toggle-${sectionName.split(" ")[0].toLowerCase()}`}
              onClick={() => toggleSection(sectionName)}
              aria-expanded={!isCollapsed}
              className="group w-full flex items-center justify-between gap-3 border-b border-[hsl(var(--rule))] pb-2 hover:border-[hsl(var(--forest))] transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-[hsl(var(--ink-soft))] group-hover:text-[hsl(var(--forest))] transition-colors">
                  {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                </span>
                <h2 className="text-2xl">{sectionName}</h2>
              </div>
              <div className="text-xs text-[hsl(var(--ink-soft))]">
                {activeCount}/{rows.length} visible
              </div>
            </button>
            {!isCollapsed && (
              <div className="mt-4 space-y-2">
              {rows.map((it) => {
                const d = draft[it.id] || {};
                return (
                  <div key={it.id} data-testid={`menu-row-${it.id}`} className={`border border-[hsl(var(--rule))] ${it.active ? "bg-[hsl(var(--bg-2))]" : "bg-[hsl(var(--rule))]/40"} p-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-center`}>
                    <div className="sm:col-span-1 flex items-center justify-center">
                      <DietaryMark value={it.dietary} size={16} />
                    </div>
                    <input className="menu-inp sm:col-span-2" list="menu-sections-all" value={d.section || ""} onChange={(e) => update(it.id, "section", e.target.value)} />
                    <input className="menu-inp sm:col-span-2" value={d.name || ""} onChange={(e) => update(it.id, "name", e.target.value)} />
                    <input className="menu-inp sm:col-span-3" placeholder="Description" value={d.description || ""} onChange={(e) => update(it.id, "description", e.target.value)} />
                    <select data-testid={`menu-dietary-${it.id}`} className="menu-inp sm:col-span-1" value={d.dietary ?? ""} onChange={(e) => update(it.id, "dietary", e.target.value)}>
                      <option value="veg">Veg</option>
                      <option value="non-veg">Non-veg</option>
                      <option value="">—</option>
                    </select>
                    <input className="menu-inp sm:col-span-1" placeholder="Price" value={d.price || ""} onChange={(e) => update(it.id, "price", e.target.value)} />
                    <input type="number" className="menu-inp sm:col-span-1" value={d.order ?? 0} onChange={(e) => update(it.id, "order", e.target.value)} />
                    <div className="flex gap-2 sm:col-span-1 justify-end">
                      <button data-testid={`menu-save-${it.id}`} onClick={() => save(it.id)} className="p-2 rounded bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" title="Save"><Save size={12} /></button>
                      <button data-testid={`menu-toggle-${it.id}`} onClick={() => toggle(it)} className="p-2 rounded border border-[hsl(var(--rule))]" title={it.active ? "Hide" : "Show"}>
                        {it.active ? <EyeOff size={12} /> : <Eye size={12} />}
                      </button>
                      <button data-testid={`menu-delete-${it.id}`} onClick={() => remove(it.id)} className="p-2 rounded border border-[hsl(6_62%_45%)] text-[hsl(6_62%_45%)]" title="Delete"><Trash2 size={12} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
          );
        })}
      </div>

      <style>{`
        .menu-inp {
          width: 100%;
          background: hsl(var(--bg));
          border: 1px solid hsl(var(--rule));
          border-radius: 4px;
          padding: 0.5rem 0.7rem;
          font-family: inherit;
          font-size: 0.85rem;
          color: hsl(var(--ink));
        }
        .menu-inp:focus { outline: none; border-color: hsl(var(--forest)); }
      `}</style>
    </div>
  );
}
